/**
 * UserModals Component
 * Tous les modals pour la gestion des utilisateurs
 */

import { useState, useEffect } from 'react';
import { X, AlertTriangle, Loader2, Shield, User as UserIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import type { UserAdminDTO, RoleDTO, MettreAJourUtilisateurData } from '../../application/dto/dtos';
import { useRoles } from '../../infrastructure/api/useRoles';

// ========================================
// DELETE USER MODAL
// ========================================

interface DeleteUserModalProps {
  user: UserAdminDTO | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

export function DeleteUserModal({ user, isOpen, onClose, onConfirm }: DeleteUserModalProps) {
  const [deleting, setDeleting] = useState(false);

  if (!isOpen || !user) return null;

  const handleConfirm = async () => {
    setDeleting(true);
    try {
      await onConfirm();
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erreur lors de la suppression de l\'utilisateur');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-lg max-w-md w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-full bg-danger-light">
            <AlertTriangle className="w-6 h-6 text-danger" />
          </div>
          <h3 className="text-lg font-semibold text-content">
            Supprimer l'utilisateur
          </h3>
        </div>

        <p className="text-content-muted mb-6">
          Êtes-vous sûr de vouloir supprimer <strong>{user.prenom} {user.nom}</strong> ({user.email}) ?
          Cette action est irréversible et supprimera toutes les données associées.
        </p>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={deleting}
            className="flex-1 px-4 py-2 border border-border
                     text-content-muted rounded-md hover:bg-surface-raised
                     transition-colors disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={handleConfirm}
            disabled={deleting}
            className="flex-1 px-4 py-2 bg-danger text-content-inverted rounded-md
                     hover:bg-danger/90 transition-colors disabled:opacity-50
                     flex items-center justify-center gap-2"
          >
            {deleting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Suppression...
              </>
            ) : (
              'Supprimer'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ========================================
// CHANGE ROLE MODAL (dynamique via API)
// ========================================

interface ChangeRoleModalProps {
  user: UserAdminDTO | null;
  isOpen: boolean;
  onClose: () => void;
  /** Reçoit l'idRole (string ID de la DB), pas un enum */
  onConfirm: (newIdRole: string) => Promise<void>;
}

export function ChangeRoleModal({ user, isOpen, onClose, onConfirm }: ChangeRoleModalProps) {
  const [selectedIdRole, setSelectedIdRole] = useState<string | null>(null);
  const [changing, setChanging] = useState(false);

  // Chargement dynamique des rôles depuis l'API
  const { data: roles, isLoading: rolesLoading } = useRoles();

  if (!isOpen || !user) return null;

  const handleConfirm = async () => {
    if (!selectedIdRole) return;

    setChanging(true);
    try {
      await onConfirm(selectedIdRole);
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erreur lors du changement de rôle');
    } finally {
      setChanging(false);
    }
  };

  const activeRoles: RoleDTO[] = roles?.filter((r: RoleDTO) => r.isActive && r.slug !== 'super-admin') ?? [];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-lg max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-content">
            Changer le rôle
          </h3>
          <button
            onClick={onClose}
            className="p-1 text-content-subtle hover:text-content-muted"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-content-muted mb-4">
          Utilisateur: <strong>{user.prenom} {user.nom}</strong>
        </p>
        <p className="text-sm text-content-muted mb-6">
          Rôle actuel: <strong>{user.roleNom ?? user.role}</strong>
        </p>

        {rolesLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-accent" />
            <span className="ml-2 text-content-muted">Chargement des rôles...</span>
          </div>
        ) : (
          <div className="space-y-2 mb-6 max-h-64 overflow-y-auto">
            {activeRoles.map((role: RoleDTO) => {
              const isSelected = selectedIdRole === role.idRole;
              const isCurrent = user.idRole === role.idRole;

              return (
                <button
                  key={role.idRole}
                  onClick={() => setSelectedIdRole(role.idRole)}
                  disabled={isCurrent}
                  className={`w-full flex items-center gap-3 p-4 rounded-lg border-2 transition-all
                           ${isCurrent
                              ? 'border-border bg-surface-overlay opacity-50 cursor-not-allowed'
                              : isSelected
                              ? 'border-primary bg-primary-light'
                              : 'border-border hover:border-border-strong'
                            }`}
                >
                  <Shield className="w-5 h-5 text-accent flex-shrink-0" />
                  <div className="flex-1 text-left">
                    <div className="font-medium text-content">
                      {role.nom}
                    </div>
                    <div className="text-xs text-content-subtle flex items-center gap-2">
                      <span>Niveau {role.niveau}</span>
                      {role.isSystemRole && (
                        <span className="px-1 bg-warning-light text-warning rounded text-xs">Système</span>
                      )}
                      {isCurrent && (
                        <span className="px-1 bg-accent-light text-accent rounded text-xs">Actuel</span>
                      )}
                    </div>
                    {role.description && (
                      <div className="text-xs text-content-subtle mt-0.5">
                        {role.description}
                      </div>
                    )}
                  </div>
                  <UserIcon className="w-4 h-4 text-content-subtle flex-shrink-0" />
                  <span className="text-xs text-content-subtle">{role.nombreUtilisateurs ?? 0}</span>
                </button>
              );
            })}
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={changing}
            className="flex-1 px-4 py-2 border border-border
                     text-content-muted rounded-md hover:bg-surface-raised
                     transition-colors disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={handleConfirm}
            disabled={changing || !selectedIdRole || selectedIdRole === user.idRole}
            className="flex-1 px-4 py-2 bg-primary text-content-inverted rounded-md
                     hover:bg-primary-hover transition-colors disabled:opacity-50
                     flex items-center justify-center gap-2"
          >
            {changing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Changement...
              </>
            ) : (
              'Confirmer'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ========================================
// EDIT USER MODAL
// ========================================

interface EditUserModalProps {
  user: UserAdminDTO | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (updatedData: MettreAJourUtilisateurData) => Promise<void>;
  organisations?: string[];
}

export function EditUserModal({ user, isOpen, onClose, onConfirm, organisations = [] }: EditUserModalProps) {
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    organisation: '',
    poste: '',
  });
  const [saving, setSaving] = useState(false);

  // Initialize form data when user changes
  useEffect(() => {
    if (user) {
      setFormData({
        nom: user.nom,
        prenom: user.prenom,
        email: user.email,
        organisation: user.organisation || '',
        poste: user.poste || '',
      });
    }
  }, [user]);

  if (!isOpen || !user) return null;

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onConfirm(formData);
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erreur lors de la mise à jour de l\'utilisateur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-content">
            Modifier l'utilisateur
          </h3>
          <button
            onClick={onClose}
            className="p-1 text-content-subtle hover:text-content-muted"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-content-muted mb-1">
                Prénom *
              </label>
              <input
                type="text"
                value={formData.prenom}
                onChange={(e) => handleChange('prenom', e.target.value)}
                required
                className="w-full px-3 py-2 border border-border rounded-md
                         bg-surface text-content
                         focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-content-muted mb-1">
                Nom *
              </label>
              <input
                type="text"
                value={formData.nom}
                onChange={(e) => handleChange('nom', e.target.value)}
                required
                className="w-full px-3 py-2 border border-border rounded-md
                         bg-surface text-content
                         focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-content-muted mb-1">
              Email *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              required
              className="w-full px-3 py-2 border border-border rounded-md
                       bg-surface text-content
                       focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-content-muted mb-1">
              Organisation
            </label>
            {organisations.length > 0 ? (
              <select
                value={formData.organisation}
                onChange={(e) => handleChange('organisation', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md
                         bg-surface text-content
                         focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="">— Aucune —</option>
                {organisations.map((org) => (
                  <option key={org} value={org}>{org}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={formData.organisation}
                onChange={(e) => handleChange('organisation', e.target.value)}
                placeholder="Nom de l'organisation"
                className="w-full px-3 py-2 border border-border rounded-md
                         bg-surface text-content
                         focus:ring-2 focus:ring-primary focus:border-primary"
              />
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-content-muted mb-1">
              Poste
            </label>
            <input
              type="text"
              value={formData.poste}
              onChange={(e) => handleChange('poste', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md
                       bg-surface text-content
                       focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="flex-1 px-4 py-2 border border-border
                       text-content-muted rounded-md hover:bg-surface-raised
                       transition-colors disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-primary text-content-inverted rounded-md
                       hover:bg-primary-hover transition-colors disabled:opacity-50
                       flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                'Enregistrer'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
